import { useState, useCallback } from 'react';
import { api, formatCurrency } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast, Toaster } from 'sonner';
import QrScannerModal from '../components/QrScannerModal';
import { Camera, Check, Gift, Search, AlertTriangle, XCircle, Clock } from 'lucide-react';

const STATUT_INFO = {
  CREE: { icon: Clock, color: 'text-blue-400', bg: 'bg-blue-400/15', label: 'Créé' },
  EN_ATTENTE: { icon: Clock, color: 'text-orange-400', bg: 'bg-orange-400/15', label: 'En attente' },
  UTILISE: { icon: Check, color: 'text-green-400', bg: 'bg-green-400/15', label: 'Déjà utilisé' },
  EXPIRE: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/15', label: 'Expiré' },
};

export default function ScanBonPublic() {
  const [showScanner, setShowScanner] = useState(false);
  const [bon, setBon] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);

  const lookupBon = useCallback(async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setBon(null);
    setResult(null);
    try {
      const res = await api.get(`/bons-cadeaux/scan/${encodeURIComponent(code.trim())}`);
      setBon(res.data);
    } catch {
      toast.error('Bon non trouvé');
      setBon(null);
    }
    setLoading(false);
  }, []);

  const handleScan = useCallback((decodedText) => {
    setShowScanner(false);
    setManualCode(decodedText);
    lookupBon(decodedText);
  }, [lookupBon]);

  const handleValidate = async () => {
    if (!bon) return;
    setValidating(true);
    try {
      const res = await api.put(`/bons-cadeaux/${bon.id}/valider`);
      if (res.data.error) {
        setResult({ success: false, message: res.data.error });
        toast.error(res.data.error);
      } else {
        setResult({ success: true, message: res.data.message });
        toast.success(res.data.message);
        setBon({ ...bon, statut: 'UTILISE' });
      }
    } catch {
      setResult({ success: false, message: 'Erreur lors de la validation' });
      toast.error('Erreur');
    }
    setValidating(false);
  };

  const reset = () => {
    setBon(null);
    setResult(null);
    setManualCode('');
  };

  const canValidate = bon && (bon.statut === 'CREE' || bon.statut === 'EN_ATTENTE');
  const statutInfo = bon ? STATUT_INFO[bon.statut] || STATUT_INFO.CREE : null;

  return (
    <div className="min-h-screen bg-[#0A0F1E] flex flex-col items-center px-4 py-8" data-testid="scan-bon-public">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C9A96E]/20 to-[#C9A96E]/5 flex items-center justify-center mb-4 border border-[#C9A96E]/20">
          <Gift size={28} className="text-[#C9A96E]" />
        </div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">Le Mas</h1>
        <p className="text-sm text-[#8892A8] mt-1">Validation Bon Cadeau</p>
      </div>

      {/* Scanner button */}
      {!bon && (
        <div className="w-full max-w-sm space-y-4">
          <Button
            onClick={() => setShowScanner(true)}
            className="w-full h-14 bg-[#C9A96E] text-[#0A0F1E] hover:bg-[#C9A96E]/90 rounded-xl text-base font-medium"
            data-testid="open-scanner-btn"
          >
            <Camera size={20} className="mr-2" /> Scanner le QR Code
          </Button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#1E2740]" />
            <span className="text-[10px] text-[#5A6478] uppercase tracking-widest">ou</span>
            <div className="flex-1 h-px bg-[#1E2740]" />
          </div>

          <div className="flex gap-2">
            <Input
              value={manualCode}
              onChange={e => setManualCode(e.target.value)}
              placeholder="N° du bon (ex: LM-202604-...)"
              className="bg-[#111827] border-[#1E2740] text-white placeholder:text-[#3A4560]"
              onKeyDown={e => e.key === 'Enter' && lookupBon(manualCode)}
              data-testid="manual-code-input"
            />
            <Button
              onClick={() => lookupBon(manualCode)}
              disabled={loading || !manualCode.trim()}
              className="bg-[#1E2740] text-[#C9A96E] hover:bg-[#263050] px-4"
              data-testid="search-bon-btn"
            >
              <Search size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="mt-8 text-[#8892A8] text-sm animate-pulse">Recherche en cours...</div>
      )}

      {/* Bon found */}
      {bon && (
        <div className="w-full max-w-sm mt-2 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="bon-result">
          <div className="bg-[#111827] rounded-2xl border border-[#1E2740] overflow-hidden">
            {/* Montant */}
            <div className="text-center py-6 border-b border-[#1E2740]">
              <p className="text-5xl font-bold text-[#C9A96E] font-mono">{bon.montant}€</p>
              <p className="text-xs text-[#5A6478] mt-2 font-mono">{bon.numero}</p>
            </div>

            {/* Statut */}
            <div className="px-4 py-3 flex items-center justify-center gap-2 border-b border-[#1E2740]">
              {statutInfo && (
                <span className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-full ${statutInfo.bg} ${statutInfo.color}`} data-testid="bon-statut">
                  <statutInfo.icon size={14} />
                  {statutInfo.label}
                </span>
              )}
            </div>

            {/* Details */}
            <div className="px-4 py-3 space-y-2 text-sm">
              {bon.beneficiaire && (
                <div className="flex justify-between">
                  <span className="text-[#5A6478]">Bénéficiaire</span>
                  <span className="text-white font-medium">{bon.beneficiaire}</span>
                </div>
              )}
              {bon.client_nom && (
                <div className="flex justify-between">
                  <span className="text-[#5A6478]">Offert par</span>
                  <span className="text-[#8892A8]">{bon.client_nom}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-[#5A6478]">Expire le</span>
                <span className="text-[#8892A8] font-mono">{bon.date_expiration}</span>
              </div>
              {bon.message && (
                <p className="text-xs italic text-[#C9A96E]/70 pt-1 text-center">"{bon.message}"</p>
              )}
            </div>
          </div>

          {/* Result message */}
          {result && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${result.success ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`} data-testid="validation-result">
              {result.success ? <Check size={18} /> : <AlertTriangle size={18} />}
              <span>{result.message}</span>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            {canValidate && !result?.success && (
              <Button
                onClick={handleValidate}
                disabled={validating}
                className="w-full h-12 bg-green-500 text-white hover:bg-green-600 rounded-xl text-base font-medium"
                data-testid="validate-bon-btn"
              >
                <Check size={20} className="mr-2" /> {validating ? 'Validation...' : 'Valider et encaisser'}
              </Button>
            )}
            {bon.statut === 'UTILISE' && !result && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-orange-400/10 text-orange-400 text-sm" data-testid="already-used-warning">
                <AlertTriangle size={16} />
                <span>Ce bon a déjà été utilisé{bon.date_utilisation ? ` le ${bon.date_utilisation}` : ''}</span>
              </div>
            )}
            {bon.statut === 'EXPIRE' && !result && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-red-400/10 text-red-400 text-sm">
                <XCircle size={16} />
                <span>Ce bon est expiré</span>
              </div>
            )}
            <Button
              onClick={reset}
              variant="ghost"
              className="w-full text-[#5A6478] hover:text-white text-sm"
              data-testid="scan-another-btn"
            >
              <Camera size={16} className="mr-2" /> Scanner un autre bon
            </Button>
          </div>
        </div>
      )}

      {/* QR Scanner Modal */}
      <QrScannerModal
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScan}
      />

      {/* Footer */}
      <p className="mt-auto pt-8 text-[10px] text-[#3A4560]">Le Mas — Validation interne</p>
    </div>
  );
}
